import { useState, useEffect, useCallback } from "react";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Capacitor } from "@capacitor/core";
import { addLog } from "@/logger";

interface PhotoState {
  imageUrl: string; // File path; data URL is cached at save time for upload
  fileName: string;
  id: string;
}

const PHOTOS_STORAGE_KEY = "grievance-photos";

/** Directory for file ops: use string fallback on web where Directory.Data can be undefined */
const DATA_DIR = typeof Directory !== "undefined" && Directory.Data != null ? Directory.Data : "DATA";

// Cache: read results + data URLs stored at save time so upload works even when Filesystem read fails (e.g. web)
const photoDataUrlCache = new Map<string, string>();

export function usePhotoState() {
  const [photos, setPhotos] = useState<PhotoState[]>([]);

  // Load photos from localStorage on mount
  useEffect(() => {
    const savedPhotos = localStorage.getItem(PHOTOS_STORAGE_KEY);
    if (savedPhotos) {
      try {
        const parsedPhotos = JSON.parse(savedPhotos) as PhotoState[];
        setPhotos(parsedPhotos);
      } catch (error) {
        console.error("Error loading saved photos:", error);
      }
    }
  }, []);

  const loadPhotoAsUrl = useCallback(async (filePathOrDataUrl: string): Promise<string> => {
    // If it's already a data URL (e.g. web fallback), return as-is
    if (filePathOrDataUrl.startsWith("data:")) {
      return filePathOrDataUrl;
    }
    const cached = photoDataUrlCache.get(filePathOrDataUrl);
    if (cached != null) return cached;
    try {
      addLog(`loadPhotoAsUrl reading: ${filePathOrDataUrl}`);
      const file = await Filesystem.readFile({
        path: filePathOrDataUrl,
        directory: DATA_DIR as Directory,
      });
      const data = typeof file.data === "string" ? file.data : "";
      const dataUrl = data.startsWith("data:") ? data : `data:image/jpeg;base64,${data}`;
      if (data.length > 0) photoDataUrlCache.set(filePathOrDataUrl, dataUrl);
      addLog(`loadPhotoAsUrl ok: ${filePathOrDataUrl} (${data.length} chars)`);
      return dataUrl;
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      addLog(`loadPhotoAsUrl FAILED: ${filePathOrDataUrl} - ${msg}`);
      console.error("Failed to load photo from filesystem", error);
      return "";
    }
  }, []);

  const savePhoto = async (imageUrl: string, fileName?: string) => {
    const base64Data = imageUrl.split(",")[1]?.trim(); // Strip data URL prefix
    const finalFileName = fileName || `grievance-photo-${Date.now()}.jpeg`;

    if (!base64Data || base64Data.length === 0) {
      console.error("savePhoto: no image data (empty base64)");
      throw new Error("No image data to save");
    }

    const isNative = Capacitor.isNativePlatform();
    if (isNative) {
      try {
        await Filesystem.writeFile({
          path: finalFileName,
          data: base64Data,
          directory: DATA_DIR as Directory,
        });
        // Cache so submit can use it even if read fails later
        photoDataUrlCache.set(finalFileName, imageUrl);
      } catch (error) {
        console.error("Error saving photo to filesystem", error);
        throw error;
      }
    } else {
      // Web: don't rely on Filesystem (can be flaky). Store data URL in cache keyed by path.
      photoDataUrlCache.set(finalFileName, imageUrl);
    }

    const photoData: PhotoState = {
      imageUrl: finalFileName,
      fileName: finalFileName,
      id: Date.now().toString(),
    };

    const updatedPhotos = [...photos, photoData];
    setPhotos(updatedPhotos);
    localStorage.setItem(PHOTOS_STORAGE_KEY, JSON.stringify(updatedPhotos));
  };

  const removePhoto = async (photoId: string) => {
    const photoToRemove = photos.find((p) => p.id === photoId);
    if (photoToRemove) {
      photoDataUrlCache.delete(photoToRemove.imageUrl);
      photoDataUrlCache.delete(photoToRemove.fileName);
      if (Capacitor.isNativePlatform()) {
        try {
          await Filesystem.deleteFile({
            path: photoToRemove.fileName,
            directory: DATA_DIR as Directory,
          });
        } catch (err) {
          console.warn("Failed to delete photo file", err);
        }
      }
    }

    const updatedPhotos = photos.filter((photo) => photo.id !== photoId);
    setPhotos(updatedPhotos);
    localStorage.setItem(PHOTOS_STORAGE_KEY, JSON.stringify(updatedPhotos));
  };

  const clearPhotos = async () => {
    photoDataUrlCache.clear();
    if (Capacitor.isNativePlatform()) {
      for (const photo of photos) {
        try {
          await Filesystem.deleteFile({
            path: photo.fileName,
            directory: DATA_DIR as Directory,
          });
        } catch (err) {
          console.warn("Error deleting file during clear", err);
        }
      }
    }

    setPhotos([]);
    localStorage.removeItem(PHOTOS_STORAGE_KEY);
  };

  return {
    photos,
    savePhoto,
    removePhoto,
    clearPhotos,
    loadPhotoAsUrl,
    hasPhotos: photos.length > 0,
    photoCount: photos.length,
  };
}
